/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback } from 'react';

import { UseUpdateAgentReturnType } from './useUpdateAgent.decl';

import { SUCCESS_FEEDBACK_MESSAGES } from '@constants/translation.constants';
import {
  getKeysWithDifferentValues,
  getKeysWithSameValues,
  removeFieldsFromObjectRecursively,
  capitalizeFirstLetter,
} from '@dashboard/library';
import { UpdateAgentMutation, UpdateAgentMutationVariables } from '@generated/UpdateAgentMutation';
import { UserFullFragment, UserFullFragment_businessHours } from '@generated/UserFullFragment';
import { UPDATE_AGENT_MUTATION } from '@graphql/mutations/UpdateAgentMutation';
import { getTrackSettingsProperties } from '@helpers/tracker.helpers';
import { useGlobalData } from '@hooks/useGlobalData/useGlobalData';
import { useGraphMutationWithFeedback } from '@hooks/useMutation';
import { useTranslation } from 'react-i18next';

/**
 * Hook to update an agent given an agent.
 * @param agent - agent to update
 * @returns an object holding a function responsible for updating an agent's working hours
 */
export function useUpdateAgent(user: UserFullFragment): UseUpdateAgentReturnType {
  const { currentUser } = useGlobalData();
  const { t } = useTranslation();
  const [mutate] = useGraphMutationWithFeedback<UpdateAgentMutation, UpdateAgentMutationVariables>(
    UPDATE_AGENT_MUTATION,
    { type: 'form' }
  );

  const updateOpeningHours = useCallback(
    async (businessHours: UserFullFragment_businessHours) => {
      const isOwnUser = currentUser.ID === user.ID;
      const updatedFields = getKeysWithDifferentValues(businessHours, user.businessHours);

      const newBusinessHours = removeFieldsFromObjectRecursively<UserFullFragment_businessHours>(
        businessHours,
        [...getKeysWithSameValues(businessHours, user.businessHours), '__typename']
      );

      const { error } = await mutate(
        {
          variables: {
            input: {
              ID: user.ID,
              businessHours: newBusinessHours,
            },
          },
        },
        {
          successMessage: t(SUCCESS_FEEDBACK_MESSAGES.UPDATE_SUCCESS, {
            name: t('user_profile.working_hours.title'),
          }),
          trackerProperties: updatedFields.map((field) =>
            getTrackSettingsProperties({
              event: 'user_settings_edited',
              settingsSection: 'Working hours',
              setting: capitalizeFirstLetter(field),
              field,
              isOwnUser,
            })
          ),
        }
      );

      return error;
    },
    [user, currentUser.ID, mutate, t]
  );

  return { updateOpeningHours };
}
